// LiveStreamAnimationGift.jsx
import React, { useRef, useEffect, useContext, useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { PlayerContext } from '../component/LiveStream.jsx';
import getAPNG from '../resource/getAPNG.js';
import PAGCanvas from '../component/PAGCanvas.jsx';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import trackMixpanel, { EventTypes } from '../resource/mixpanel.js';

function getContainedSize({
  childWidth,
  childHeight,
  parentWidth,
  parentHeight,
}) {
  const childRatio = childWidth / childHeight;
  const parentRatio = parentWidth / parentHeight;

  const isChildWiderThanParent = childRatio > parentRatio;

  return {
    width: isChildWiderThanParent ? parentWidth : parentHeight * childRatio,
    height: isChildWiderThanParent ? parentWidth / childRatio : parentHeight,
  };
}

export const LiveStreamAnimationGift = ({
  byteplusPlayerVideoWidth = 0,
  byteplusPlayerVideoHeight = 0,
  giftNoticeId = '',
  giftId = '',
  streamId = '',
  isSender = false,
  giftImage = '',
  pagImage = '',
  isPagGift = false,
  shouldShowMainAsset = false,
  wrapperRef = EMPTY_OBJECT,
}) => {
  const canvasRef = useRef();
  const player = useContext(PlayerContext)?.player;

  const handlePagPlay = useCallback(() => {
    if (!isSender) return;
    trackMixpanel({
      type: EventTypes.LIVESTREAM_GIFT_ANIMATION_PLAYED,
      payload: {
        giftId,
        streamId,
        animationType: 'pag',
      },
    });
  }, [giftId, streamId, isSender]);

  let byteplusPlayerDisplayWidth = 0,
    byteplusPlayerDisplayHeight = 0;

  if (wrapperRef.current) {
    const result = getContainedSize({
      childWidth: byteplusPlayerVideoWidth,
      childHeight: byteplusPlayerVideoHeight,
      parentWidth: wrapperRef.current.clientWidth,
      parentHeight: wrapperRef.current.clientHeight,
    });
    byteplusPlayerDisplayWidth = result.width;
    byteplusPlayerDisplayHeight = result.height;
  }

  // get video screen size without black bars
  const streamingScreenWidth =
    player?.getVideoElement?.().offsetWidth ||
    player?.currentWidth?.() ||
    byteplusPlayerDisplayWidth ||
    0;
  const streamingScreenHeight =
    player?.getVideoElement?.().offsetHeight ||
    player?.currentHeight?.() ||
    byteplusPlayerDisplayHeight ||
    0;

  const originalVideoWidth =
    player?.videoWidth?.() ||
    player?.getBitrateInfoListFor?.('video')?.[0]?.width ||
    byteplusPlayerVideoWidth ||
    0;
  const originalVideoHeight =
    player?.videoHeight?.() ||
    player?.getBitrateInfoListFor?.('video')?.[0]?.height ||
    byteplusPlayerVideoHeight ||
    0;

  const { width: actualVideoWidth, height: actualVideoHeight } =
    getContainedSize({
      parentHeight: streamingScreenHeight,
      parentWidth: streamingScreenWidth,
      childHeight: originalVideoHeight,
      childWidth: originalVideoWidth,
    });

  useEffect(() => {
    async function playAnimationGift() {
      const { isAPNG, apng } = await getAPNG({ imageSource: giftImage });
      if (!isAPNG || !canvasRef.current) {
        return;
      }
      canvasRef.current.width = apng.width;
      canvasRef.current.style.width = `${apng.width / 2}px`;
      canvasRef.current.style.maxWidth = `${actualVideoWidth}px`;

      canvasRef.current.height = apng.height;
      canvasRef.current.style.height = `${apng.height / 2}px`;
      canvasRef.current.style.maxHeight = `${actualVideoHeight}px`;

      const player = await apng.getPlayer(canvasRef.current.getContext('2d'));
      player.play();
      if (isSender) {
        trackMixpanel({
          type: EventTypes.LIVESTREAM_GIFT_ANIMATION_PLAYED,
          payload: {
            giftId,
            streamId,
            animationType: 'apng',
          },
        });
      }
    }

    // Only play APNG animation if it's not a PAG gift
    if (giftImage && shouldShowMainAsset && !isPagGift) {
      playAnimationGift();
    }
  }, [
    giftImage,
    giftNoticeId,
    giftId,
    streamId,
    isSender,
    actualVideoWidth,
    actualVideoHeight,
    shouldShowMainAsset,
    isPagGift,
  ]);

  if ((!giftImage && !pagImage) || !shouldShowMainAsset) return null;

  return (
    <StyledLiveStreamAnimationGift>
      {isPagGift && pagImage ? (
        <>
          <PAGCanvas
            key={`pag-${giftNoticeId}`}
            src={pagImage}
            isPlaying={true}
            onPlay={handlePagPlay}
            repeat={1}
            className="pag-gift-animation"
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          />
        </>
      ) : (
        <>
          <GiftCanvas ref={canvasRef} />
        </>
      )}
    </StyledLiveStreamAnimationGift>
  );
};

LiveStreamAnimationGift.propTypes = {
  byteplusPlayerVideoWidth: PropTypes.number,
  byteplusPlayerVideoHeight: PropTypes.number,
  giftNoticeId: PropTypes.string,
  giftId: PropTypes.string,
  streamId: PropTypes.string,
  isSender: PropTypes.bool,
  giftImage: PropTypes.string,
  pagImage: PropTypes.string,
  isPagGift: PropTypes.bool,
  shouldShowMainAsset: PropTypes.bool,
  wrapperRef: PropTypes.object,
};

const StyledLiveStreamAnimationGift = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const GiftCanvas = styled.canvas`
  object-fit: contain;
`;

export default LiveStreamAnimationGift;
